import "./PickYourFocus.css";
import casinoImage from "../assets/casino.jpg";
import sportsbookImage from "../assets/sportsbook.png";
import lotteryImage from "../assets/lottery.png";
import sweepstakesImage from "../assets/sweepstakes.png";

// Configuration interface for full customization
export interface PickYourFocusConfig {
  colors: {
    background: string;
    headerText: string;
    subtitleText: string;
    cardBackground: string;
    cardTitleText: string;
    cardDescriptionText: string;
    casinoBorderGlow: string;
    sportsbookBorderGlow: string;
    lotteryBorderGlow: string;
    sweepstakesBorderGlow: string;
  };
  textSizes: {
    header: string;
    subtitle: string;
    cardTitle: string;
    cardDescription: string;
  };
  content: {
    header: string;
    subtitle: string;
    categories: Array<{
      id: string;
      title: string;
      description: string;
      borderGlowColor?: string;
      imageUrl?: string;
    }>;
  };
}

// Default configuration
const defaultConfig: PickYourFocusConfig = {
  colors: {
    background: "transparent", // Use platform's continuous background
    headerText: "#ffffff", // White color for main heading
    subtitleText: "rgba(255, 255, 255, 0.8)", // White with opacity for subtitle
    cardBackground: "rgba(var(--color-bg1), 0.1)", // Semi-transparent with theme colors
    cardTitleText: "#ffffff", // White color for card titles
    cardDescriptionText: "rgba(255, 255, 255, 0.9)", // White with slight opacity for descriptions
    casinoBorderGlow: "rgb(var(--color1))",
    sportsbookBorderGlow: "rgb(var(--color2))",
    lotteryBorderGlow: "rgb(var(--color1))",
    sweepstakesBorderGlow: "rgb(var(--color2))",
  },
  textSizes: {
    header: "3.5rem",
    subtitle: "1.125rem",
    cardTitle: "2rem",
    cardDescription: "0.95rem",
  },
  content: {
    header: "Pick Your Focus",
    subtitle: "Sportsbook, Casino, Sweepstakes, or Lottery—choose your model.",
    categories: [
      {
        id: "casino",
        title: "Casino",
        description: "Over 15'000 most popular games included",
        borderGlowColor: "#8b5cf6",
      },
      {
        id: "sportsbook",
        title: "Sportsbook",
        description:
          "Thousands of Sports and Esports events and Virtual sports available",
        borderGlowColor: "#3b82f6",
      },
      {
        id: "lottery",
        title: "Lottery",
        description: "over 30 most popular lotteries available worldwide",
        borderGlowColor: "#10b981",
      },
      {
        id: "sweepstakes",
        title: "Sweepstakes",
        description: "The best solution for the US market",
        borderGlowColor: "#f59e0b",
      },
    ],
  },
};

interface PickYourFocusProps {
  config?: Partial<PickYourFocusConfig>;
}

export default function PickYourFocus({ config }: PickYourFocusProps) {
  // Merge custom config with defaults
  const finalConfig: PickYourFocusConfig = {
    colors: { ...defaultConfig.colors, ...config?.colors },
    textSizes: { ...defaultConfig.textSizes, ...config?.textSizes },
    content: {
      header: config?.content?.header || defaultConfig.content.header,
      subtitle: config?.content?.subtitle || defaultConfig.content.subtitle,
      categories: (config?.content?.categories || defaultConfig.content.categories).map((cat, index) => ({
        ...cat,
        borderGlowColor: cat.borderGlowColor || defaultConfig.content.categories[index]?.borderGlowColor || defaultConfig.colors.casinoBorderGlow,
      })),
    },
  };

  const { colors, textSizes, content } = finalConfig;

  const getPreviewImage = (category: { id: string; imageUrl?: string }): string => {
    // If imageUrl is provided and valid, use it
    if (category.imageUrl && category.imageUrl.trim() !== "") {
      // If it's already a full URL, use it as is
      if (category.imageUrl.startsWith("http")) {
        return category.imageUrl;
      }
      // Otherwise, construct the full URL
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8080";
      return `${apiUrl}${category.imageUrl}`;
    }
    
    // Fallback to default images
    switch (category.id) {
      case "casino":
        return casinoImage;
      case "sportsbook":
        return sportsbookImage;
      case "lottery":
        return lotteryImage;
      case "sweepstakes":
        return sweepstakesImage;
      default:
        return "";
    }
  };

  return (
    <div
      className="pick-your-focus"
      style={{
        background: colors.background,
      }}
    >
      <div className="header-section">
        <h1
          className="main-header"
          style={{
            color: colors.headerText,
            fontSize: textSizes.header,
          }}
        >
          {content.header}
        </h1>
        <p
          className="subtitle"
          style={{
            color: colors.subtitleText,
            fontSize: textSizes.subtitle,
          }}
        >
          {content.subtitle}
        </p>
      </div>

      <div className="categories-grid">
        {content.categories.map((category) => (
          <div key={category.id} className="category-wrapper">
            <div className="card-info">
              <h2
                className="card-title"
                style={{
                  color: colors.cardTitleText,
                  fontSize: textSizes.cardTitle,
                }}
              >
                {category.title}
              </h2>
              <p
                className="card-description"
                style={{
                  color: colors.cardDescriptionText,
                  fontSize: textSizes.cardDescription,
                }}
              >
                {category.description}
              </p>
            </div>
            <div
              className="category-card"
              style={{
                background: colors.cardBackground,
                boxShadow: category.borderGlowColor
                  ? `0 0 60px ${category.borderGlowColor}80, 0 0 100px ${category.borderGlowColor}40, inset 0 0 0 1px ${category.borderGlowColor}CC`
                  : `0 0 60px ${colors.casinoBorderGlow}80, 0 0 100px ${colors.casinoBorderGlow}40, inset 0 0 0 1px ${colors.casinoBorderGlow}CC`,
              }}
            >
              <div className="card-preview">
                <img
                  src={getPreviewImage(category)}
                  alt={`${category.title} preview`}
                  className="preview-image"
                  onError={(e) => {
                    // Fallback to default image if API image fails
                    const target = e.target as HTMLImageElement;
                    switch (category.id) {
                      case "casino":
                        target.src = casinoImage;
                        break;
                      case "sportsbook":
                        target.src = sportsbookImage;
                        break;
                      case "lottery":
                        target.src = lotteryImage;
                        break;
                      case "sweepstakes":
                        target.src = sweepstakesImage;
                        break;
                    }
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
